import * as React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { FaOptionIconProps } from '@europrocurement/l2d-icons';
import { Link } from 'react-router-dom';
import BreadcrumbDropdown, {
    BreadcrumbDropdownProps,
} from '../BreadcrumbDropdown/BreadcrumbDropdown';

type PathAndLinkStandard = {
    path?: string;
    link: string;
    icon?: FaOptionIconProps;
};

type PathAndLinkDropdown = {
    path?: string;
    link: string;
    icon?: FaOptionIconProps;
    dropdown: BreadcrumbDropdownProps;
};

type PathItem = PathAndLinkStandard | PathAndLinkDropdown | string;

export type LazyBreadcrumbsProps = {
    path: Array<PathItem>;
    bypass?: boolean;
    sx?: React.CSSProperties;
    separator?: string;
    maxWidth?: string;
};

const hasDropdown = (item: PathItem): item is PathAndLinkDropdown =>
    typeof item === 'object' && 'dropdown' in item;

export const LazyBreadcrumb: React.FunctionComponent<LazyBreadcrumbsProps> = function (props) {
    const { path, bypass, sx, separator = ' / ', maxWidth = '70%' } = props;
    const theme = useTheme();

    const textGris = theme.palette.text.gris50;
    const primaryText = theme.palette.text.primary;
    const hoverBg = theme.palette.grey[200];
    const activeBg = theme.palette.grey[200];

    // Filtrer les éléments vides à la fin
    const validPath = path.filter((item, index) => {
        if (index >= path.length - 1) return false;
        return item !== '' && item != null;
    });

    return (
        <Box
            className="breadcrumb"
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                width: '100%',
                marginTop: '10px',
                ...sx,
            }}
            role="navigation"
        >
            {/* Container horizontal pour les breadcrumbs */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'nowrap',
                    gap: 0.5,
                    maxWidth,
                    minWidth: 0,
                    overflow: 'hidden',
                }}
            >
                {validPath.map((uniquePath, index) => {
                    const label =
                        typeof uniquePath === 'object' ? uniquePath?.path || '' : uniquePath;
                    const link = typeof uniquePath === 'object' ? uniquePath?.link : '';

                    const itemKey =
                        typeof uniquePath === 'object' ? `${label}-${link}` : `text-${label}`;

                    const isLast = index === validPath.length - 1;

                    // Séparateur (sauf pour le premier élément)
                    const separatorElement = validPath.indexOf(uniquePath) > 0 && (
                        <Typography
                            key={`separator-${itemKey}`}
                            sx={{
                                color: textGris,
                                fontSize: '14px',
                                fontWeight: 400,
                                flexShrink: 0,
                            }}
                        >
                            {separator}
                        </Typography>
                    );

                    // Gestion du dropdown
                    if (hasDropdown(uniquePath)) {
                        return (
                            <React.Fragment key={`dropdown-${itemKey}`}>
                                {separatorElement}
                                <Box sx={{ minWidth: 0, flexShrink: 1 }}>
                                    <BreadcrumbDropdown
                                        {...uniquePath.dropdown}
                                        isActive={isLast}
                                    />
                                </Box>
                            </React.Fragment>
                        );
                    }

                    // Gestion standard
                    if (typeof uniquePath === 'object') {
                        return (
                            <React.Fragment key={`item-${itemKey}`}>
                                {separatorElement}
                                <Link
                                    to={uniquePath.link}
                                    style={{
                                        textDecoration: 'none',
                                        minWidth: 0,
                                        flexShrink: 1,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            padding: '6px 8px',
                                            borderRadius: '6px',
                                            backgroundColor: isLast ? activeBg : 'transparent',
                                            transition: 'all 0.2s ease-in-out',
                                            '&:hover': {
                                                backgroundColor: hoverBg,
                                                '& .breadcrumb-text': {
                                                    color: primaryText,
                                                },
                                            },
                                        }}
                                    >
                                        <Typography
                                            className="breadcrumb-text"
                                            sx={{
                                                color: textGris,
                                                fontSize: '14px',
                                                lineHeight: '24px',
                                                fontWeight: 400,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                transition: 'color 0.2s ease-in-out',
                                            }}
                                        >
                                            {label}
                                        </Typography>
                                    </Box>
                                </Link>
                            </React.Fragment>
                        );
                    }

                    return (
                        <React.Fragment key={`text-${itemKey}`}>
                            {separatorElement}
                            <Box
                                sx={{
                                    padding: '6px 8px',
                                    borderRadius: '6px',
                                    backgroundColor: isLast ? activeBg : 'transparent',
                                }}
                            >
                                <Typography
                                    sx={{
                                        color: textGris,
                                        fontSize: '14px',
                                        fontWeight: 400,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        minWidth: 0,
                                        flexShrink: 1,
                                    }}
                                >
                                    {label}
                                </Typography>
                            </Box>
                        </React.Fragment>
                    );
                })}
            </Box>

            {/* Titre de la page courante */}
            {!bypass && path[path.length - 1] && path[path.length - 1] !== '' && (
                <Typography
                    variant="h1"
                    color="secondary"
                    className="page-name"
                    sx={{
                        mt: 1,
                        fontSize: '24px',
                        fontWeight: 600,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: '100%',
                    }}
                >
                    {`${path[path.length - 1]}`}
                </Typography>
            )}
        </Box>
    );
};
